<script setup lang="ts">
  import {
    type SuperUserPhoneNumberRequest as PhoneNumberRequest,
    PhoneNumbersService,
    OrganisationsService,
    type SuperUserRetellAgent as RetellAgent,
    type MinimalOrganisation,
    RetellAgentsService
  } from '@/types/generated'
  import { ref, onMounted, computed } from 'vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import * as yup from 'yup'
  import router from '@/router'
  import { ROUTES } from '@/router/routes'
  import { Form, type GenericObject } from 'vee-validate'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import { createSuccessToast } from '@/lib/toasts'
  import { useTranslation } from '@/composables/useTranslation'
  import { type Option } from '@/components/admin/form/SelectField.vue'

  const isDev = import.meta.env.VITE_ENV === 'development'

  const LANGUAGE_STYLES: Record<string, { label: string; classes: string }> = {
    de: { label: 'DE', classes: 'bg-purple-50 text-purple-700 border border-purple-200' },
    en: { label: 'EN', classes: 'bg-emerald-50 text-emerald-700 border border-emerald-200' },
    fr: { label: 'FR', classes: 'bg-violet-50 text-violet-700 border border-violet-200' },
    pl: { label: 'PL', classes: 'bg-blue-50 text-blue-700 border border-blue-200' }
  }

  const { t } = useTranslation({
    en: {
      retellAgent: 'Retell agent',
      customer: 'Customer',
      create: 'Create',
      attention: 'Attention',
      attentionText:
        'Creating a phone number will register it with an external system which will incur costs.',
      developer: 'Developer'
    },
    de: {
      customer: 'Kunde',
      create: 'Erstellen',
      attention: 'Achtung',
      attentionText:
        'Das Erstellen einer Telefonnummer registriert sie bei einem externen System, welches Kosten verursacht.',
      developer: 'Entwickler'
    }
  })

  const retellAgentsList = ref<RetellAgent[] | null>(null)

  const parseDeveloper = (name: string | null | undefined): string => {
    if (!name) return 'Other'
    if (name.startsWith('Staging')) return 'Staging'
    const match = name.match(/^(.+?)(?:'s\s|'\s| Local Dev| Testing Agent| Voice)/)
    if (match) return match[1].trim()
    return 'Other'
  }

  const selectedDeveloper = ref<string | null>(null)

  const developerNames = computed<string[]>(() => {
    if (!isDev || !retellAgentsList.value) return []
    const devs = new Set<string>()
    retellAgentsList.value.forEach((agent) => {
      devs.add(parseDeveloper(agent.agent_name))
    })
    return [...devs].sort()
  })

  const filteredAgents = computed<RetellAgent[]>(() => {
    if (!retellAgentsList.value) return []
    if (!isDev || !selectedDeveloper.value) return retellAgentsList.value
    return retellAgentsList.value.filter(
      (agent) => parseDeveloper(agent.agent_name) === selectedDeveloper.value
    )
  })

  const agentByUuid = computed<Record<string, RetellAgent>>(() => {
    const map: Record<string, RetellAgent> = {}
    retellAgentsList.value?.forEach((agent) => {
      map[agent.uuid] = agent
    })
    return map
  })

  const retellAgentOptions = computed<Option[]>(() => {
    return filteredAgents.value.map((agent) => ({
      value: agent.uuid,
      title: agent.agent_name ?? agent.retell_agent_id
    }))
  })

  const getLanguageBadges = (uuid: string) => {
    const agent = agentByUuid.value[uuid]
    if (!agent) return []
    const name = agent.agent_name ?? ''
    // Parse explicit multi-language codes from name: "(DE + EN)", "(FR + DE)", etc.
    const multiMatch = name.match(/\(([A-Z]{2}(?:\s*\+\s*[A-Z]{2})+)\)/)
    if (multiMatch) {
      return multiMatch[1]
        .split('+')
        .map((c) => c.trim().toLowerCase())
        .filter((c) => LANGUAGE_STYLES[c])
    }
    // Single language from stored field
    if (agent.language && LANGUAGE_STYLES[agent.language]) {
      return [agent.language]
    }
    return []
  }

  const organisationsList = ref<MinimalOrganisation[]>([])
  const organisationOptions = computed<Option[]>(() => {
    return (
      organisationsList.value?.map((organisation) => {
        return {
          value: organisation.uuid,
          title: organisation.name ?? ''
        }
      }) ?? []
    )
  })

  const isProcessing = ref(false)

  const handleSubmit = async (values: GenericObject) => {
    isProcessing.value = true
    PhoneNumbersService.phoneNumbersCreate({ requestBody: values as PhoneNumberRequest })
      .then((phoneNumber) => {
        createSuccessToast()
        router.push(ROUTES.ADMIN.VOICE.PHONE_NUMBER_DETAIL.replace(':uuid', phoneNumber.uuid))
      })
      .finally(() => {
        isProcessing.value = false
      })
  }

  onMounted(async () => {
    // Both endpoints return plain arrays (no pagination) — cast since OpenAPI generates paginated types
    const agentsResp = await RetellAgentsService.retellAgentsList()
    retellAgentsList.value = (agentsResp.results ?? agentsResp) as unknown as RetellAgent[]
    const orgsResp = await OrganisationsService.organisationsOrganisationsListList()
    organisationsList.value = orgsResp as unknown as MinimalOrganisation[]
  })
</script>

<template>
  <div class="space-y-5 p-2 sm:p-5 sm:py-0 md:pt-5">
    <!-- Page Header -->
    <div class="flex items-center justify-between gap-x-5">
      <Breadcrumbs
        :items="[
          { title: 'Dashboard', to: ROUTES.ADMIN.DASHBOARD },
          { title: 'Phone numbers', to: ROUTES.ADMIN.VOICE.PHONE_NUMBER_LIST },
          { title: 'Create phone number', to: ROUTES.ADMIN.VOICE.PHONE_NUMBER_CREATE }
        ]"
      />
      <div class="flex items-center justify-end gap-x-2"></div>
    </div>

    <div
      class="flex flex-col rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-neutral-700
        dark:bg-neutral-800"
    >
      <div class="w-full md:w-1/2">
        <Form
          v-slot="{ errors }"
          @submit="handleSubmit"
        >
          <div
            class="border-s-4 border-red-500 bg-red-50 p-4 dark:bg-red-800/30"
            aria-live="polite"
            tabindex="-1"
            aria-labelledby="hs-bordered-red-style-label"
          >
            <div class="flex">
              <div class="ms-3">
                <h3
                  id="hs-bordered-red-style-label"
                  class="font-semibold text-gray-800 dark:text-white"
                >
                  {{ t('attention') }}
                </h3>
                <p class="text-sm text-gray-700 dark:text-neutral-400">
                  {{ t('attentionText') }}
                </p>
              </div>
            </div>
          </div>

          <!-- Developer filter (dev environment only) -->
          <div
            v-if="isDev && developerNames.length > 0"
            class="mt-4"
          >
            <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-neutral-300">
              {{ t('developer') }}
            </label>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="dev in developerNames"
                :key="dev"
                type="button"
                class="inline-flex items-center rounded-full px-3 py-1.5 text-sm font-medium transition-colors"
                :class="
                  selectedDeveloper === dev
                    ? 'bg-purple-600 text-white shadow-sm'
                    : `bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-neutral-700 dark:text-neutral-300
                      dark:hover:bg-neutral-600`
                "
                @click="selectedDeveloper = selectedDeveloper === dev ? null : dev"
              >
                {{ dev }}
                <span
                  v-if="selectedDeveloper === dev"
                  class="ml-1.5 text-xs opacity-75"
                >
                  &times;
                </span>
              </button>
            </div>
          </div>

          <SelectField
            :label="t('customer')"
            :is-filterable="true"
            name="org_uuid"
            :rules="yup.string().uuid().required()"
            :options="organisationOptions"
            :disabled="isProcessing"
          />
          <SelectField
            :label="t('retellAgent')"
            :is-filterable="true"
            name="agent_uuid"
            :rules="yup.string().uuid().required()"
            :options="retellAgentOptions"
            :disabled="isProcessing"
          >
            <template #option-contents="{ option }">
              <div class="flex w-full items-center justify-between">
                <div class="flex items-center gap-2">
                  <span class="text-gray-800">{{ option.title }}</span>
                  <span
                    v-for="lang in getLanguageBadges(option.value as string)"
                    :key="lang"
                    class="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold leading-none"
                    :class="LANGUAGE_STYLES[lang]?.classes"
                  >
                    {{ LANGUAGE_STYLES[lang]?.label }}
                  </span>
                </div>
              </div>
            </template>
          </SelectField>
          <button
            class="mt-3 inline-flex items-center gap-x-2 rounded-lg border border-transparent bg-purple-600 px-3 py-2
              text-sm font-semibold text-white hover:bg-purple-700 focus:outline-none focus:ring-2
              focus:ring-blue-500 disabled:pointer-events-none disabled:opacity-50"
            type="submit"
            :disabled="Object.keys(errors).length > 0 || isProcessing"
          >
            {{ t('create') }}
          </button>
        </Form>
      </div>
    </div>
  </div>
</template>
